import { Box, ButtonBase, Popover, Tooltip, Typography } from '@mui/material';
import { ChevronRight } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import type { RoiId } from '~/domain/changes/ids';
import { useToolOptionsDockRef } from '~/components/toolbar/shared/toolOptionsDockContext';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { toolSurfaceSx } from '~/theme/theme';
import {
    boundingRoiConstraintState,
    collisionConstraintState,
} from '~/state/atoms/toolsAtoms';
import InsideConstraintSection from '../drawingConstraints/InsideConstraintSection';
import OutsideConstraintSection from '../drawingConstraints/OutsideConstraintSection';
import IntensityThresholdSection, {
    thresholdRangeText,
} from '../drawingConstraints/IntensityThresholdSection';
import { useIntensityThreshold } from '../drawingConstraints/useIntensityThreshold';
import {
    countModifiers,
    thresholdApplies,
} from '../drawingConstraints/useActiveModifierCount';
import {
    ConstraintSection,
    ConstraintWarningIcon,
    ModifierCountText,
} from '../drawingConstraints/constraintParts';
import {
    computeApprovedProtectionState,
    constraintCandidates,
    insideConstraintInactive,
    outsideConstraintInactive,
    pickDefaultBoundingRoi,
    protectedCandidateIds,
    toggleProtectAllApproved,
} from '../drawingConstraints/collisionSelection';

interface DrawingConstraintsControlProps {
    /** Disable interaction (e.g. no editable ROI selected). */
    disabled?: boolean;
    /**
     * Brush erase is active. Inside/Outside are persisted but do not apply to
     * an erase stroke (the engine short-circuits them), so the sections show a
     * "doesn't apply while erasing" note instead of pretending to be live.
     */
    erasing?: boolean;
}

/** The modifier sections, of which one at a time shows its settings. */
type ConstraintSectionId = 'inside' | 'outside' | 'threshold';

/** Width of the modifiers panel, in CSS pixels. */
const MODIFIERS_PANEL_WIDTH_PX = 280;

/**
 * Unified "drawing constraints" surface: a row under the tool's options that
 * reads how many modifiers apply, and how many are on but apply to nothing,
 * and opens the modifiers panel beside the tool's options. The panel holds
 * one section per modifier, and one section at a time shows its settings.
 *
 *  - Inside (bounding): clip drawing to the interior of one structure.
 *  - Outside (collision): subtract a set of protected structures from the
 *    stroke so it cannot overwrite a neighbour.
 *  - Threshold (intensity): keep drawing only on voxels within an intensity
 *    range (HU for CT, SUVbw for PET).
 *
 * Each publishes to recoil. The armed tool takes them, and the draw action
 * applies them as one SDF step at commit. This component reads and sets the
 * shared Inside/Outside state; `useKeepDrawingConstraintsValid` keeps their
 * cross-constraint invariants whether or not it is mounted. Each section
 * renders from props (Threshold via {@link useIntensityThreshold}).
 */
export default function DrawingConstraintsControl({
    disabled = false,
    erasing = false,
}: DrawingConstraintsControlProps) {
    const intl = useIntl();
    // The panel opens beside the dock that holds the row, or beside the row
    // outside a dock.
    const dockRef = useToolOptionsDockRef();
    const [panelAnchor, setPanelAnchor] = useState<HTMLElement | null>(null);
    const open = panelAnchor !== null;
    const [expanded, setExpanded] = useState<ConstraintSectionId | null>(
        'outside'
    );
    const expandedChange =
        (section: ConstraintSectionId) => (isExpanded: boolean) =>
            setExpanded(isExpanded ? section : null);

    const [bounding, setBounding] = useRecoilState(boundingRoiConstraintState);
    const [collision, setCollision] = useRecoilState(collisionConstraintState);
    const { structureSet, roiId: selectedRoiId } = useActiveEditTarget();

    const threshold = useIntensityThreshold();

    // Candidate structures for both constraints: the selected structure and
    // the point ROIs are not offered.
    const candidates = useMemo(
        () => constraintCandidates(structureSet?.roiArray ?? [], selectedRoiId),
        [structureSet, selectedRoiId]
    );
    const protectedCandidates = protectedCandidateIds(
        collision.roiIds,
        candidates
    );

    const thresholdOn = thresholdApplies(threshold);
    const modifierCount = countModifiers({
        inside: bounding,
        outside: collision,
        threshold,
        candidates,
    });

    // The structure currently acting as the "Inside" bound. It must not also
    // be a protected "Outside" structure: clipping the stroke to a structure
    // and then subtracting that same structure would erase the whole stroke.
    // So it is disabled in the Outside list, and
    // useKeepDrawingConstraintsValid takes it out of the protected set.
    const activeBoundingRoi = bounding.enabled ? bounding.roiId : null;

    // Turned on with no structure, Inside takes the default bound. It is
    // never chosen for the user at any other time.
    const handleToggleInside = useCallback(
        (enabled: boolean) =>
            setBounding((c) => ({
                enabled,
                roiId:
                    enabled && c.roiId === null
                        ? pickDefaultBoundingRoi(candidates)
                        : c.roiId,
            })),
        [candidates, setBounding]
    );
    const handleSelectInside = useCallback(
        (roiId: RoiId) => setBounding((c) => ({ ...c, roiId })),
        [setBounding]
    );

    const handleToggleOutside = useCallback(
        (enabled: boolean) => setCollision((c) => ({ ...c, enabled })),
        [setCollision]
    );
    const handleToggleProtected = useCallback(
        (roiId: RoiId, checked: boolean) =>
            setCollision((c) => ({
                ...c,
                roiIds: checked
                    ? [...c.roiIds, roiId]
                    : c.roiIds.filter((n) => n !== roiId),
            })),
        [setCollision]
    );
    const approvedRoiIds = useMemo(
        () =>
            candidates
                .filter((r) => r.isApproved && r.roiId !== activeBoundingRoi)
                .map((r) => r.roiId),
        [candidates, activeBoundingRoi]
    );
    const { hasApproved, allApprovedProtected, someApprovedProtected } =
        computeApprovedProtectionState(approvedRoiIds, collision.roiIds);

    // "Protect all approved" checkbox: add (checked) or remove (unchecked)
    // every approved structure from the protected set, leaving any
    // manually-checked non-approved structures untouched.
    const handleToggleProtectAllApproved = useCallback(
        (checked: boolean) =>
            setCollision((c) => ({
                ...c,
                roiIds: toggleProtectAllApproved(
                    c.roiIds,
                    approvedRoiIds,
                    checked
                ),
            })),
        [approvedRoiIds, setCollision]
    );

    // Enabled-but-inactive states: an enabled constraint with nothing it can
    // actually apply. Surfaced so an enabled protection never appears to be
    // doing nothing (the engine also logs the unresolved ROI). The reason
    // names the structure being edited when that is what it is set to.
    const insideInactiveReasonId = !insideConstraintInactive(
        bounding,
        candidates
    )
        ? undefined
        : bounding.roiId === selectedRoiId
          ? 'constraintInactiveInsideEdited'
          : 'constraintInactiveInside';
    const outsideInactiveReasonId = !outsideConstraintInactive(
        collision,
        candidates
    )
        ? undefined
        : collision.roiIds.length > 0 &&
            collision.roiIds.every((n) => n === selectedRoiId)
          ? 'constraintInactiveOutsideEdited'
          : 'constraintInactiveOutside';

    const off = intl.formatMessage({ id: 'constraintSummary.off' });
    const boundingRoiName = candidates.find(
        (r) => r.roiId === bounding.roiId
    )?.name;
    const insideSummary = !bounding.enabled
        ? off
        : (boundingRoiName ??
          intl.formatMessage({ id: 'boundingConstraintPlaceholder' }));
    const manuallyProtected = protectedCandidates.filter(
        (n) => !approvedRoiIds.includes(n)
    ).length;
    const outsideSummary = !collision.enabled
        ? off
        : hasApproved && allApprovedProtected
          ? intl.formatMessage(
                { id: 'constraintSummary.approvedProtected' },
                { count: manuallyProtected }
            )
          : intl.formatMessage(
                { id: 'constraintSummary.protected' },
                { count: protectedCandidates.length }
            );
    const thresholdSummary =
        thresholdOn && threshold.hasRange ? thresholdRangeText(threshold) : off;

    return (
        <>
            <Tooltip
                title={intl.formatMessage({ id: 'constraintsTooltip' })}
                placement="right"
            >
                <ButtonBase
                    onClick={(event) =>
                        setPanelAnchor(
                            open
                                ? null
                                : (dockRef.current ?? event.currentTarget)
                        )
                    }
                    disabled={disabled}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    data-testid="tool-drawing-constraints"
                    sx={{
                        width: '100%',
                        height: 32,
                        px: 1.5,
                        gap: 1,
                        justifyContent: 'flex-start',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        '&:hover': { bgcolor: 'action.hover' },
                        '&.Mui-disabled': { opacity: 0.5 },
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            flex: 1,
                            textAlign: 'left',
                            fontSize: 10,
                            fontWeight: 500,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: 'text.muted',
                        }}
                    >
                        <FormattedMessage id="constraintsButtonLabel" />
                    </Typography>
                    <Typography
                        component="span"
                        sx={(theme) => ({
                            fontFamily: theme.typography.fontFamilyMonospace,
                            fontSize: 10,
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            color: 'primary.light',
                        })}
                    >
                        <ModifierCountText count={modifierCount} readsNone />
                    </Typography>
                    <Box
                        component="span"
                        sx={{ display: 'flex', color: 'text.muted' }}
                    >
                        <ChevronRight size={14} />
                    </Box>
                </ButtonBase>
            </Tooltip>
            <Popover
                open={open}
                anchorEl={panelAnchor}
                onClose={() => setPanelAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: [
                            toolSurfaceSx,
                            {
                                // As far from the options as a menu opens
                                // from its button.
                                ml: 0.5,
                                width: MODIFIERS_PANEL_WIDTH_PX,
                            },
                        ],
                    },
                }}
            >
                <ConstraintSection
                    first
                    labelId="boundingConstraintLabel"
                    tooltipId="boundingConstraintTooltip"
                    summary={insideSummary}
                    on={bounding.enabled}
                    onToggle={handleToggleInside}
                    warning={
                        insideInactiveReasonId !== undefined && (
                            <ConstraintWarningIcon
                                titleId={insideInactiveReasonId}
                                placement="left"
                            />
                        )
                    }
                    expanded={expanded === 'inside'}
                    onExpandedChange={expandedChange('inside')}
                >
                    <InsideConstraintSection
                        disabled={!bounding.enabled}
                        roiId={bounding.roiId}
                        candidates={candidates}
                        inactiveReasonId={insideInactiveReasonId}
                        erasing={erasing}
                        onSelect={handleSelectInside}
                    />
                </ConstraintSection>
                <ConstraintSection
                    labelId="collisionConstraintLabel"
                    tooltipId="collisionConstraintTooltip"
                    summary={outsideSummary}
                    on={collision.enabled}
                    onToggle={handleToggleOutside}
                    warning={
                        outsideInactiveReasonId !== undefined && (
                            <ConstraintWarningIcon
                                titleId={outsideInactiveReasonId}
                                placement="left"
                            />
                        )
                    }
                    expanded={expanded === 'outside'}
                    onExpandedChange={expandedChange('outside')}
                >
                    <OutsideConstraintSection
                        disabled={!collision.enabled}
                        candidates={candidates}
                        activeBoundingRoi={activeBoundingRoi}
                        protectedRoiIds={collision.roiIds}
                        hasApproved={hasApproved}
                        allApprovedProtected={allApprovedProtected}
                        someApprovedProtected={someApprovedProtected}
                        erasing={erasing}
                        onToggleProtected={handleToggleProtected}
                        onToggleProtectAllApproved={
                            handleToggleProtectAllApproved
                        }
                    />
                </ConstraintSection>
                <ConstraintSection
                    labelId="thresholdConstraintLabel"
                    tooltipId={threshold.labelTooltipId}
                    summary={thresholdSummary}
                    on={thresholdOn}
                    switchDisabled={!threshold.available}
                    onToggle={threshold.onToggle}
                    warning={
                        !threshold.available && (
                            <ConstraintWarningIcon
                                titleId={threshold.unavailableTooltipId}
                                placement="left"
                            />
                        )
                    }
                    expanded={expanded === 'threshold'}
                    onExpandedChange={expandedChange('threshold')}
                >
                    <IntensityThresholdSection {...threshold} />
                </ConstraintSection>
            </Popover>
        </>
    );
}
